// Create/edit screen for a Shared Setting. Wires the three step sections and the
// completion rail onto the form state, the mapping engine and the header-key
// lookup — no form logic of its own.

import React from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';

import PaddedForm from '../../../components/PaddedForm';
import {
  BreadcrumbsBar,
  FormCard,
  FormPageTitle,
  FormTitleRow,
  PageLayout,
} from '../style';
import AttributeMappings from './AttributeMappings';
import BasicInformation from './BasicInformation';
import CompletionRail from './CompletionRail';
import IntegrationsPicker from './IntegrationsPicker';
import useHeaderKeys from './useHeaderKeys';
import useIntegrationSearch from './useIntegrationSearch';
import useMappingEngine from './useMappingEngine';
import useSharedSettingForm from './useSharedSettingForm';

export default function SharedSettingForm({ editingId = null, onBack }) {
  const form = useSharedSettingForm({ editingId, onBack });
  const search = useIntegrationSearch();
  const headerKeysByAttr = useHeaderKeys(form.selectedIntegrations);
  const engine = useMappingEngine({
    selectedIntegrations: form.selectedIntegrations,
    mappings: form.mappings,
    setMappings: form.setMappings,
    savedIntegrationIds: form.savedIntegrationIds,
  });

  return (
    <>
      <BreadcrumbsBar
        path={[{ to: '/toolkit/shared_settings', text: t`Shared Settings` }]}
        current={editingId ? t`Edit Shared Setting` : t`Create Shared Setting`}
      />
      {/* Saving goes through the Save button, never a native submit. Enter in the
          integration search would otherwise submit the form and reload the page. */}
      <PaddedForm onSubmit={(e) => e.preventDefault()}>
        <PageLayout>
          <div>
            <FormTitleRow>
              <FormPageTitle>
                {editingId ? (
                  <Trans>Edit Shared Setting</Trans>
                ) : (
                  <Trans>Create Shared Setting</Trans>
                )}
              </FormPageTitle>
            </FormTitleRow>

            <FormCard>
              <BasicInformation
                name={form.name}
                nameError={form.nameError}
                onNameChange={form.changeName}
                editing={Boolean(editingId)}
                saveDisabled={form.loading || form.noMappingsMade}
                onSave={form.save}
                onCancel={onBack}
              />

              <IntegrationsPicker
                search={search}
                selectedIntegrations={form.selectedIntegrations}
                integError={form.integError}
                loading={form.loading}
                onAdd={form.addIntegration}
                onRemove={form.removeIntegration}
              />

              {form.integOk && (
                <AttributeMappings
                  engine={engine}
                  headerKeysByAttr={headerKeysByAttr}
                  noMappingsMade={form.noMappingsMade}
                />
              )}
            </FormCard>
          </div>

          <CompletionRail
            name={form.name}
            nameOk={form.nameOk}
            integOk={form.integOk}
            selectedCount={form.selectedIntegrations.length}
            mappedCount={engine.mappedCount}
            totalMappings={engine.totalMappings}
            mappingsComplete={engine.mappingsComplete}
            mappedPct={engine.mappedPct}
          />
        </PageLayout>
      </PaddedForm>
    </>
  );
}

SharedSettingForm.propTypes = {
  editingId: PropTypes.string,
  onBack: PropTypes.func.isRequired,
};
